import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { PaymentOption } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { FixedBottomBarContainer, NoddiButton, noddiToast } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { commonTrackingEvents, toFailureProps, tracking, useAuthContext } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useTireOfferStore } from '../../stores/tireOfferStore';
import { NavioContactChatWidget } from '../BookingFlow/Common/NavioContactChatWidget';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { FlowBackButton } from '../Layouts/FlowBackButton';

interface AcceptTireOfferButtonProps {
  userComment: string;
  slug: string;
  paymentMethod?: PaymentOption;
  /** Where the tires are mounted; unset keeps the offer's own (tire hotel offers have none). */
  addressId: number | undefined;
  disabled?: boolean;
}

export const AcceptTireOfferButton = ({
  userComment,
  slug,
  paymentMethod,
  addressId,
  disabled
}: AcceptTireOfferButtonProps) => {
  const { currentUserGroupId: userGroupId } = useAuthContext();
  const { selectedQuoteItem } = useTireOfferStore();
  const navigate = useNavigate();
  const {
    mutateAsync: confirmTireQuoteOffer,
    isPending: isConfirmTireQuoteOfferPending,
    error: confirmTireQuoteOfferError
  } = noddiAsync.usePost({
    type: URLKeys.postConfirmTireQuoteOffer,
    queryConfig: {
      onError: (error) => {
        tracking.track(commonTrackingEvents.tireOfferAcceptFailed, toFailureProps(error, 'accept'));
      },
      onSuccess: async (data) => {
        tracking.track(commonTrackingEvents.tireOfferAccepted, {
          slug,
          quoteItemId: selectedQuoteItem?.id,
          paymentOption: data.data.paymentMethod
        });
        invalidateQueryExactMatch({
          urlKey: URLKeys.getCarTireQuotesIgnoringImpersonation,
          input: { userGroupIds: [userGroupId!] }
        });
        if (data.data.paymentMethod === PaymentOption.PAY_BEFORE) {
          const sessionId = data.data.paymentSessionProfile?.sessionId;
          if (sessionId != null && sessionId !== '') {
            navigate(customerRoutes.tireOfferConfirmation.getPath({ sessionId }));
          } else {
            noddiToast.error(t`Payment could not be started. Please try again or contact support.`);
            navigate(customerRoutes.tireOfferInvoicedOrderConfirmed.getPath({ slug }));
          }
        } else {
          navigate(customerRoutes.tireOfferInvoicedOrderConfirmed.getPath({ slug }));
        }
      }
    }
  });

  if (!selectedQuoteItem) {
    return null;
  }

  return (
    <FixedBottomBarContainer contentWidth='md'>
      <div className='flex w-full flex-col gap-3'>
        {confirmTireQuoteOfferError && <ApiErrorMessageWithTrans error={confirmTireQuoteOfferError} />}
        <div className='flex items-center justify-between gap-2'>
          <FlowBackButton />
          {/* Right of the action on phones, beside Back from md. */}
          <NavioContactChatWidget size='small' className='max-md:order-last' />
          <NoddiButton
            className='ml-auto'
            variant='success'
            loading={isConfirmTireQuoteOfferPending}
            disabled={disabled}
            // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs confirmTireQuoteOffer(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onClick={async () => {
              await confirmTireQuoteOffer({
                carTireQuoteItemId: selectedQuoteItem.id,
                quoteItemTires: selectedQuoteItem.quoteItemTires.map((tire) => ({
                  quoteItemTireId: tire.id,
                  quantity: tire.quantitySelected ?? 0
                })),
                comments: userComment,
                addressId
              });
            }}
          >
            {paymentMethod === PaymentOption.PAY_BEFORE ? (
              <Trans>Confirm and go to payment</Trans>
            ) : (
              <Trans>Confirm order</Trans>
            )}
          </NoddiButton>
        </div>
      </div>
    </FixedBottomBarContainer>
  );
};
